/**
 * Edit Company Modal
 *
 * Modal for editing existing company details.
 * Organization number is displayed as readonly since it cannot be changed.
 */

import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { X, AlertCircle, Building2, MapPin, Hash } from "lucide-react";
import { toast } from "sonner";
import { Spinner } from "@/components/ui/spinner";
import { Button } from "@/components/ui/button";
import { companiesApi } from "@/lib/api";

const editCompanySchema = z.object({
  name: z.string().min(1, "Företagsnamn krävs").max(255),
  type: z.enum(["AB", "EF", "HB", "KB"]),
  seatMunicipality: z.string().max(100).optional(),
  address: z.string().max(255).optional(),
  postalCode: z.string().max(10).optional(),
  city: z.string().max(100).optional(),
});

type EditCompanyData = z.infer<typeof editCompanySchema>;

interface EditCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: {
    id: string;
    name: string;
    organizationNumber: string;
    type: "AB" | "EF" | "HB" | "KB";
    seatMunicipality: string | null;
    address: string | null;
    postalCode: string | null;
    city: string | null;
  };
  onUpdated: (company: any) => void;
}

export function EditCompanyModal({
  isOpen,
  onClose,
  company,
  onUpdated,
}: EditCompanyModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<EditCompanyData>({
    resolver: zodResolver(editCompanySchema),
    defaultValues: {
      name: company.name,
      type: company.type,
      seatMunicipality: company.seatMunicipality || "",
      address: company.address || "",
      postalCode: company.postalCode || "",
      city: company.city || "",
    },
  });

  // Reset form when company prop changes or modal opens
  useEffect(() => {
    if (isOpen) {
      reset({
        name: company.name,
        type: company.type,
        seatMunicipality: company.seatMunicipality || "",
        address: company.address || "",
        postalCode: company.postalCode || "",
        city: company.city || "",
      });
      setSubmitError(null);
    }
  }, [isOpen, company, reset]);

  const handleUpdate = async (data: EditCompanyData) => {
    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const response = await companiesApi.update(company.id, data);

      if (response.success && response.data) {
        toast.success("Företagsuppgifter uppdaterade");
        onUpdated(response.data.company);
        onClose();
      } else {
        setSubmitError(
          response.error?.message || "Kunde inte uppdatera företaget"
        );
      }
    } catch (err) {
      console.error("Update company error:", err);
      toast.error("Kunde inte uppdatera företaget");
      setSubmitError("Ett fel uppstod");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSubmitError(null);
    reset();
    onClose();
  };

  // Close on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") handleClose();
    };
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-lg mx-4 max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Redigera företag
          </h2>
          <button
            onClick={handleClose}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form
          onSubmit={handleSubmit(handleUpdate)}
          className="flex flex-col flex-1 overflow-hidden"
        >
          <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
            {submitError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2.5">
                <AlertCircle
                  className="text-red-600 mt-0.5 flex-shrink-0"
                  size={16}
                />
                <p className="text-sm text-red-600">{submitError}</p>
              </div>
            )}

            {/* Section: Företagsinformation */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Building2 size={16} className="text-gray-400" />
                <h3 className="text-sm font-medium text-gray-900">
                  Företagsinformation
                </h3>
              </div>

              {/* Organisationsnummer (readonly) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Organisationsnummer
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Hash size={15} className="text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={company.organizationNumber}
                    readOnly
                    disabled
                    className="input pl-9 bg-gray-50 text-gray-500 cursor-not-allowed"
                  />
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  Organisationsnummer kan inte ändras
                </p>
              </div>

              {/* Företagsnamn */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Företagsnamn *
                </label>
                <input
                  type="text"
                  {...register("name")}
                  className="input"
                  placeholder="Företaget AB"
                />
                {errors.name && (
                  <p className="text-sm text-red-600 mt-1">
                    {errors.name.message}
                  </p>
                )}
              </div>

              {/* Företagsform */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Företagsform
                </label>
                <select
                  {...register("type")}
                  className="input"
                >
                  <option value="AB">Aktiebolag (AB)</option>
                  <option value="EF">Enskild firma (EF)</option>
                  <option value="HB">Handelsbolag (HB)</option>
                  <option value="KB">Kommanditbolag (KB)</option>
                </select>
              </div>
            </div>

            {/* Section: Adressuppgifter */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-gray-400" />
                <h3 className="text-sm font-medium text-gray-900">
                  Adressuppgifter
                </h3>
              </div>

              {/* Adress */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Adress
                </label>
                <input
                  type="text"
                  {...register("address")}
                  className="input"
                  placeholder="Gatuadress"
                />
              </div>

              {/* Postnummer + Stad */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Postnummer
                  </label>
                  <input
                    type="text"
                    {...register("postalCode")}
                    className="input"
                    placeholder="123 45"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1.5">
                    Stad
                  </label>
                  <input
                    type="text"
                    {...register("city")}
                    className="input"
                    placeholder="Stockholm"
                  />
                </div>
              </div>

              {/* Län (seatMunicipality) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">
                  Län (valfritt)
                </label>
                <input
                  type="text"
                  {...register("seatMunicipality")}
                  className="input"
                  placeholder="Stockholms län"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={handleClose}>
              Avbryt
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <Spinner size="sm" /> : "Spara ändringar"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
